import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ClipboardList, FileText, Eye, Plus, X } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const SupervisorToolbox = ({ user }) => {
  const { t } = useTranslation();
  const [toolboxTalks, setToolboxTalks] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedTalk, setSelectedTalk] = useState(null);
  const [showConductDialog, setShowConductDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [conductingSession, setConductingSession] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionForm, setSessionForm] = useState({
    attendees: [],
    manualAttendees: [''],
    supervisorNotes: '',
    supervisorSignature: '',
    location: '',
    duration: ''
  });

  useEffect(() => {
    loadToolboxTalks();
    loadSessions();
  }, []);

  const loadToolboxTalks = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/my-toolbox-talks');
      setToolboxTalks(response.data);
    } catch (error) {
      console.error('Failed to load toolbox talks:', error);
      toast.error(t('supervisorPages.failedToLoadToolbox'));
    } finally {
      setLoading(false);
    }
  };

  const loadSessions = async () => {
    try {
      const response = await axiosInstance.get('/toolbox-talk-sessions');
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  const startConductSession = (talk) => {
    setSelectedTalk(talk);
    setSessionForm({
      attendees: [],
      manualAttendees: [''],
      supervisorNotes: '',
      supervisorSignature: '',
      location: '',
      duration: ''
    });
    setShowConductDialog(true);
  };

  const addManualAttendee = () => {
    setSessionForm({
      ...sessionForm,
      manualAttendees: [...sessionForm.manualAttendees, '']
    });
  };

  const updateManualAttendee = (index, value) => {
    const updated = [...sessionForm.manualAttendees];
    updated[index] = value;
    setSessionForm({ ...sessionForm, manualAttendees: updated });
  };

  const removeManualAttendee = (index) => {
    const updated = sessionForm.manualAttendees.filter((_, i) => i !== index);
    setSessionForm({ ...sessionForm, manualAttendees: updated });
  };

  const conductSession = async () => {
    if (sessionForm.manualAttendees.filter(a => a.trim()).length === 0) {
      toast.error(t('supervisorPages.addAtLeastOneAttendee'));
      return;
    }

    if (!sessionForm.supervisorSignature || sessionForm.supervisorSignature.trim() === '') {
      toast.error(t('supervisorPages.signatureRequired'));
      return;
    }

    try {
      setConductingSession(true);
      const sessionData = {
        toolboxTalkId: selectedTalk.id,
        attendees: sessionForm.attendees,
        manualAttendees: sessionForm.manualAttendees.filter(a => a.trim()),
        supervisorNotes: sessionForm.supervisorNotes,
        supervisorSignature: sessionForm.supervisorSignature,
        location: sessionForm.location,
        duration: sessionForm.duration ? parseInt(sessionForm.duration) : null
      };

      await axiosInstance.post('/toolbox-talk-sessions', sessionData);
      toast.success(t('supervisorPages.sessionRecorded'));
      setShowConductDialog(false);
      loadSessions();
    } catch (error) {
      console.error('Failed to conduct session:', error);
      toast.error(error.response?.data?.detail || t('supervisorPages.failedToRecordSession'));
    } finally {
      setConductingSession(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => window.history.back()} className="mb-4 hover:bg-gray-100" data-testid="back-button">
          {t('common.back')}
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center">
                  <ClipboardList className="w-5 h-5 mr-2 text-orange-600" />
                  {t('supervisorPages.toolboxTalks')}
                </CardTitle>
                <CardDescription>{t('supervisorPages.conductSafetyTalks')}</CardDescription>
              </div>
              <Button onClick={() => setShowHistoryDialog(true)} variant="outline" data-testid="view-history">
                <Eye className="w-4 h-4 mr-2" />
                {t('supervisorPages.viewHistory')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
              </div>
            ) : toolboxTalks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>{t('supervisorPages.noToolboxTalks')}</p>
                <p className="text-sm">{t('supervisorPages.contactAdmin')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700 mb-2">{t('supervisorPages.availableTalks')} ({toolboxTalks.length}):</p>
                {toolboxTalks.map((talk) => (
                  <div key={talk.id} className="p-3 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-gray-900 break-words">{talk.title}</p>
                          {talk.pdfPath && <FileText className="w-4 h-4 text-red-600 flex-shrink-0" />}
                          {talk.category && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded flex-shrink-0">{talk.category}</span>
                          )}
                        </div>
                        {talk.description && <p className="text-sm text-gray-500 mt-1 break-words">{talk.description}</p>}
                      </div>
                      <Button onClick={() => startConductSession(talk)} className="bg-orange-600 hover:bg-orange-700 flex-shrink-0" size="sm">
                        <ClipboardList className="w-4 h-4 mr-2" />
                        {t('supervisorPages.conduct')}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Conduct Session Dialog */}
      <Dialog open={showConductDialog} onOpenChange={setShowConductDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('supervisorPages.conductToolboxTalk')}</DialogTitle>
            <DialogDescription>{selectedTalk?.title}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2 flex items-center">
                <FileText className="w-4 h-4 mr-2" />
                {t('supervisorPages.toolboxContent')}
              </h3>
              {selectedTalk?.category && (
                <div className="mb-2">
                  <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">{selectedTalk.category}</span>
                </div>
              )}
              {selectedTalk?.description && <p className="text-sm text-gray-700 mb-3 italic">{selectedTalk.description}</p>}
              {selectedTalk?.content && (
                <div className="text-sm text-gray-800 whitespace-pre-wrap bg-white p-3 rounded border border-blue-100 max-h-60 overflow-y-auto">
                  {selectedTalk.content}
                </div>
              )}
              {selectedTalk?.pdfPath && (
                <div className="mt-2">
                  <a href={selectedTalk.pdfPath} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 underline flex items-center">
                    <FileText className="w-4 h-4 mr-1" />
                    {t('supervisorPages.viewPdfDocument')}
                  </a>
                </div>
              )}
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold text-gray-900 mb-3">{t('supervisorPages.recordSessionDetails')}</h3>
            </div>

            <div>
              <Label>{t('supervisorPages.attendees')} *</Label>
              {sessionForm.manualAttendees.map((attendee, index) => (
                <div key={index} className="flex gap-2 mt-2">
                  <Input
                    value={attendee}
                    onChange={(e) => updateManualAttendee(index, e.target.value)}
                    placeholder={t('supervisorPages.enterAttendeeName')}
                  />
                  {sessionForm.manualAttendees.length > 1 && (
                    <Button variant="outline" size="icon" onClick={() => removeManualAttendee(index)}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addManualAttendee} className="mt-2">
                <Plus className="w-4 h-4 mr-2" />
                {t('supervisorPages.addAttendee')}
              </Button>
            </div>

            <div>
              <Label htmlFor="location">{t('common.location')}</Label>
              <Input id="location" value={sessionForm.location} onChange={(e) => setSessionForm({ ...sessionForm, location: e.target.value })} placeholder={t('common.location')} />
            </div>

            <div>
              <Label htmlFor="duration">{t('supervisorPages.durationMinutes')}</Label>
              <Input id="duration" type="number" value={sessionForm.duration} onChange={(e) => setSessionForm({ ...sessionForm, duration: e.target.value })} placeholder="15" />
            </div>

            <div>
              <Label htmlFor="notes">{t('supervisorPages.supervisorNotes')}</Label>
              <Textarea id="notes" value={sessionForm.supervisorNotes} onChange={(e) => setSessionForm({ ...sessionForm, supervisorNotes: e.target.value })} rows={3} placeholder={t('supervisorPages.notesPlaceholder')} />
            </div>

            <div>
              <Label htmlFor="signature">{t('supervisorPages.supervisorSignature')} *</Label>
              <Input id="signature" value={sessionForm.supervisorSignature} onChange={(e) => setSessionForm({ ...sessionForm, supervisorSignature: e.target.value })} placeholder={t('supervisorPages.typeFullName')} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConductDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={conductSession} disabled={conductingSession} className="bg-orange-600 hover:bg-orange-700">
              {conductingSession ? t('supervisorPages.recording') : t('supervisorPages.recordSession')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('supervisorPages.toolboxHistory')}</DialogTitle>
            <DialogDescription>{t('supervisorPages.pastSessions')}</DialogDescription>
          </DialogHeader>

          {sessions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>{t('supervisorPages.noSessions')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sessions.map((session) => (
                <div key={session.id} className="border rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900">{session.toolboxTalkTitle}</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    {t('supervisorPages.conductedOn')} {new Date(session.conductedAt).toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-600">
                    {t('supervisorPages.attendees')}: {session.manualAttendees?.length || 0}
                  </p>
                  {session.location && (
                    <p className="text-sm text-gray-600">{t('common.location')}: {session.location}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupervisorToolbox;
